// src/belediye/components/mesajlasma/KonumSecici.js
// FAZ-55/5 — Detaylı görevde KONUM adımı (mahalle ve açık adresten AYRI).
//
// Kullanıcı isteği (2026-07-27): "harita sorulunca otomatikman konum bulmaya da
// harita üstünden işaretleme olacak. Otomatik konumu bulduğu yeri yuvarlak
// içine al, tam belli olsun. Bir çap belirli ona ufaktan ve değiştirilebilsin
// konumun yeri. Haritayı tam genişlik aç, gönder butonunu ileri gibi kullan."
//
// Akış: açılır açılmaz tarayıcıdan konum istenir (izin verilirse harita oraya
// gider ve nokta işaretlenir). İzin yoksa/başarısızsa akış KİLİTLENMEZ —
// haritaya tıklayarak da işaretlenir. İşaret sürüklenerek düzeltilebilir.
//
// FAZ-83/Y2: artık YerAdimi'nin bir SEKMESİ olarak da çiziliyor. O hâlde
// (`gomulu`) kendi onay satırını TAŞIMAZ — tek alt satır YerAdimi'nindir;
// işaretlenen nokta `onDegis` ile yukarı bildirilir. Sohbette "Konum gönder"
// (FAZ-66) ve eski çağıranlar hiç değişmedi.
//
// Sözleşme: onDone({ latitude, longitude }) — ikisi de sayı ya da hiç.
//           gomulu + onDegis({lat,lng}|null) — sekme kipi.
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";

import SihirbazAltSatir from "./SihirbazAltSatir";

// FAZ-55/17: harita "herhangi bir yerde" değil, sistemin BM4 varsayılan
// sınırının merkezinde (Kırıkhan) açılır — dashboard haritasıyla aynı kaynak.
import { varsayilanHaritaMerkezi, YEDEK_MERKEZ, YEDEK_ZOOM } from "./varsayilanMerkez";

// Harita AYRI paket; react-leaflet ESM olduğu için statik import bütün sohbet
// testlerini kırar (bkz. KonumHarita.js başlığı).
const KonumHarita = lazy(() => import("./KonumHarita"));

// FAZ-83/6 (kullanıcı, 2026-08-01): 250 m KALDIRILDI. Gerekçesi doğrudan
// yerleşim: "mobilde bu sebeple 'Konumumu bul' seçeneğinin tek satırda
// kalması için." Dört çap + düğme telefonda ikinci satıra taşıyordu; üç çap
// (25/50/100) saha işi için zaten yeterli hassasiyet aralığı.
const CAP_SECENEKLERI = [25, 50, 100];

export default function KonumSecici({
  initial = null,
  onDone,
  onSkip,
  // FAZ-66 — MOBİLDE OTOMATİK KONUM (WhatsApp mantığı). Sohbette "Konum
  // gönder" telefondan/tabletten açılınca cihazın konumu KENDİLİĞİNDEN aranır;
  // harita oraya gider ve nokta işaretlenir — kullanıcı yalnız onaylar.
  // Masaüstünde KAPALI kalır: FAZ-55/17'de görev sihirbazında otomatik istek
  // bilinçli kaldırılmıştı (izin penceresini zorluyor ve haritayı işle
  // ilgisiz bir yere taşıyordu). Telefonda ise konum göndermenin AMACI zaten
  // "buradayım" demek.
  otoBul = false,
  gonderEtiketi = "Bu konumu kullan",
  // FAZ-83/Y2 — sekme kipi: her işaretleme yukarı bildirilir ve alt satırın
  // düğmeleri adımın tamamına (YerAdimi) ait olur.
  gomulu = false,
  onDegis,
  altSatir = null,
}) {
  const [nokta, setNokta] = useState(initial);
  const [cap, setCap] = useState(50);
  const [gps, setGps] = useState("bekliyor"); // bekliyor | araniyor | bulundu | yok
  const [acilis, setAcilis] = useState({ merkez: YEDEK_MERKEZ, zoom: YEDEK_ZOOM });

  useEffect(() => {
    let iptal = false;
    varsayilanHaritaMerkezi().then((v) => {
      if (!iptal) setAcilis(v);
    });
    return () => {
      iptal = true;
    };
  }, []);

  // FAZ-83: her işaretleme tek kapıdan geçer — hem yerel duruma yazar hem de
  // gömülü kipte ebeveyne bildirir. İki ayrı `setNokta` çağrısı kalsaydı
  // birinden bildirmeyi unutmak an meselesiydi.
  const isaretle = useCallback(
    (p) => {
      setNokta(p);
      onDegis && onDegis(p);
    },
    [onDegis]
  );

  const konumBul = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGps("yok");
      return;
    }
    setGps("araniyor");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Math.round(pos.coords.latitude * 1e6) / 1e6;
        const lng = Math.round(pos.coords.longitude * 1e6) / 1e6;
        isaretle({ lat, lng });
        setGps("bulundu");
      },
      () => setGps("yok"),
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 0 }
    );
  }, [isaretle]);

  // FAZ-55/17: görev sihirbazında OTOMATİK GPS İSTEĞİ YOK. Kullanıcı: "harita
  // herhangi bir yerde açılmasın… Kırıkhan'dan açılsın, sonra 'konumumu bul'
  // deyince oraya gitsin." Adım açılır açılmaz konum istemek hem izin
  // penceresini zorluyor hem de haritayı görevle ilgisiz bir yere taşıyordu.
  // FAZ-66: sohbette konum GÖNDERİRKEN mobilde tam tersi doğru → otoBul.
  useEffect(() => {
    if (otoBul && !initial) konumBul();
    // yalnız açılışta: sonraki kullanıcı hareketleri haritayı geri çekmesin
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otoBul]);

  return (
    <div className="msj-konum">
      <div className="msj-konum-harita">
        <Suspense fallback={<div className="msj-mahalle-bos">Harita hazırlanıyor…</div>}>
          <KonumHarita nokta={nokta} capMetre={cap} onSec={isaretle} merkez={acilis.merkez} zoom={acilis.zoom} />
        </Suspense>
      </div>

      <div className="msj-konum-cap">
        <span className="msj-due-etiket">Çap</span>
        <div className="msj-due-cipler">
          {CAP_SECENEKLERI.map((m) => (
            <button
              key={m}
              type="button"
              className={`msj-due-cip ${cap === m ? "is-secili" : ""}`}
              onClick={() => setCap(m)}
            >
              {m} m
            </button>
          ))}
          {/* FAZ-83/6: bu düğme bir "çap seçeneği" değil, bir EYLEM — çipler
              gibi görünmesi onu kalabalığın içinde kaybediyordu. Ayrı renk
              (kullanıcı: "biraz daha belirgin yap"). */}
          <button type="button" className="msj-due-cip msj-konum-bul" onClick={konumBul}>
            <MapPinIcon className="msj-serit-ic" /> Konumumu bul
          </button>
        </div>
      </div>

      {/* FAZ-83/B1+2: onay satırı ortak ANA MESAJ KUTUSU — Atla SOL uçta,
          Gönder SAĞ uçta, ortada durumun özeti. Sekme kipinde düğmeler
          YerAdimi'nden gelir (`altSatir`), özet yine buranın işidir. */}
      <SihirbazAltSatir
        sinif="msj-konum-onay"
        {...(altSatir || {
          onSkip,
          skipTitle: "Konum işaretlemeden devam et",
          gonderAktif: !!nokta,
          onGonder: () => nokta && onDone({ latitude: nokta.lat, longitude: nokta.lng }),
          gonderTitle: gonderEtiketi,
        })}
        ozet={
          nokta ? (
            <>
              <b>İşaretlendi</b>
              {` · ${nokta.lat.toFixed(5)}, ${nokta.lng.toFixed(5)} · ${cap} m`}
            </>
          ) : gps === "araniyor" ? (
            "Konumunuz aranıyor…"
          ) : gps === "yok" ? (
            "Konum alınamadı — haritaya dokunarak işaretleyin."
          ) : (
            "Haritaya dokunun ya da “Konumumu bul”a basın."
          )
        }
      />
    </div>
  );
}
